/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import CreateSubstituteModal from './CreateSubstituteModal';
import { request } from '../../config/helpers/axios-instance';

const ItemSubstitutes = ({ itemSubstitutes = [], quotationId, prescriptionItems, onRefetch, isEditable, orderId }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingSubstitute, setEditingSubstitute] = useState(null);

    // Get unique quotation items
    const uniqueQuotationItems = React.useMemo(() => {
        const itemsArray = Array.isArray(prescriptionItems) ? prescriptionItems : Object.values(prescriptionItems || {});
        const uniqueItemsMap = new Map();

        itemsArray.forEach(item => {
            const itemId = item.itemId || item.item?.id;
            if (itemId && !uniqueItemsMap.has(itemId)) {
                uniqueItemsMap.set(itemId, item);
            }
        });

        return Array.from(uniqueItemsMap.values());
    }, [prescriptionItems]);

    // Check if all items have substitutes
    const allItemsHaveSubstitutes = React.useMemo(() => {
        if (uniqueQuotationItems.length === 0) return false;

        const usedSubstituteItemIds = new Set(
            itemSubstitutes.map(sub => sub.substitutedItemId?.toString())
        );

        // Check if every unique item is already used as a substitute
        return uniqueQuotationItems.every(item => {
            const itemId = (item.itemId || item.item?.id)?.toString();
            return usedSubstituteItemIds.has(itemId);
        });
    }, [uniqueQuotationItems, itemSubstitutes]);

    const handleDeleteSubstitute = async (substituteId) => {
        if (window.confirm('Are you sure you want to delete this substitution?')) {
            try {
                await request({
                    url: `itemSubstitute/admin/${substituteId}`,
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                    }
                });
                toast.success('Substitute item deleted successfully');
                onRefetch();
            } catch (error) {
                toast.error('Failed to delete substitute item');
                console.error('Error deleting substitute:', error);
            }
        }
    };

    const handleEditSubstitute = (substitute) => {
        setEditingSubstitute(substitute);
        setShowCreateModal(true);
    };

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        setEditingSubstitute(null);
        onRefetch();
    };

    if (!itemSubstitutes || itemSubstitutes.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Item Substitutions</h3>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white-50 text-sm font-medium rounded-md transition-colors flex items-center gap-1 disabled:opacity-50"
                        disabled={!isEditable}
                    >
                        <span>+</span>
                        Add Substitution
                    </button>
                </div>
                <p className="text-gray-500 text-center py-8">No item substitutions found</p>

                {showCreateModal && (
                    <CreateSubstituteModal
                        onClose={() => {
                            setShowCreateModal(false);
                            setEditingSubstitute(null);
                        }}
                        onSuccess={handleCreateSuccess}
                        quotationId={quotationId}
                        orderId={orderId}
                        prescriptionItems={prescriptionItems}
                        editingSubstitute={editingSubstitute}
                        existingSubstitutions={itemSubstitutes}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Item Substitutions [{itemSubstitutes.length}]</h3>
                {!allItemsHaveSubstitutes ? (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        disabled={!isEditable}
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white-50 text-sm font-medium rounded-md transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                        <span>+</span>
                        Add Substitution
                    </button>
                ) : (
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                        All items have substitutes
                    </span>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="table-auto w-full">
                    <thead>
                        <tr className="text-left h-12 font-sans border-b-2 border-gray-100">
                            <th className="pl-3 font-sans text-left px-2">Original Item</th>
                            <th className="pl-3 font-sans text-left px-2">Substituted With</th>
                            <th className="pl-3 font-sans text-left px-2">Type</th>
                            <th className="pl-3 font-sans text-left px-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="border-collapse">
                        {itemSubstitutes.map((substitute) => (
                            <tr
                                key={substitute.id}
                                className="text-left border-t border-gray-100 h-14 font-sans hover:bg-gray-50"
                            >
                                <td className="pl-3 py-2 font-sans text-left">
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-gray-800">
                                            {substitute.originalItem?.name}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            ID: {substitute.originalItem?.id}
                                        </span>
                                    </div>
                                </td>

                                <td className="pl-3 py-2 font-sans text-left">
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-green-600">
                                            {substitute.substitutedItem?.name}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            ID: {substitute.substitutedItem?.id}
                                        </span>
                                    </div>
                                </td>

                                <td className="pl-3 py-2 font-sans text-left">
                                    {substitute.type ? (
                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${substitute.type === 'SUBSTITUTION' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {substitute.type}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>

                                <td className="pl-3 py-2 font-sans text-left">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleEditSubstitute(substitute)}
                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50"
                                            disabled={!isEditable}
                                            title="Edit substitution"
                                        >
                                            <FiEdit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteSubstitute(substitute.id)}
                                            className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50"
                                            disabled={!isEditable}
                                            title="Delete substitution"
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <CreateSubstituteModal
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingSubstitute(null);
                    }}
                    onSuccess={handleCreateSuccess}
                    quotationId={quotationId}
                    orderId={orderId}
                    prescriptionItems={prescriptionItems}
                    editingSubstitute={editingSubstitute}
                    existingSubstitutions={itemSubstitutes}
                />
            )}
        </div>
    );
};

export default ItemSubstitutes;
